import { IonApp, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, RouteComponentProps } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import { StorageProvider } from "./context/StorageContext";
import { ToastProvider } from "./context/ToastContext";
import { getLastUserAction } from "./utils/userActionTracker";
import { useAppUpdate } from "./hooks/useAppUpdate";
import { initStorage } from "./hooks/storageService";
import { NutritionProvider } from "./context/NutritionContext";
import { BrazeProvider } from "./providers/BrazeProvider";

/* Lazy Load Components */
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const ActivateNotifications = lazy(
  () => import("./pages/ActivateNotifications")
);
const AfterVisitSummaries = lazy(() => import("./pages/AfterVisitSummaries"));
const AfterVisitSummaryDetails = lazy(
  () => import("./pages/AfterVisitSummaryDetails")
);
const AppointmentDetails = lazy(() => import("./pages/AppointmentDetails"));
const AppointmentDetailsOL = lazy(() => import("./pages/AppointmentDetailsOL"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const CreateAccount = lazy(() => import("./pages/CreateAccount"));
const CreateAppointment = lazy(
  () => import("./pages/create-appointment/CreateAppointment")
);
const AppointmentSelector = lazy(
  () => import("./pages/create-appointment/AppointmentSelector")
);
const ScheduleAppointment = lazy(
  () => import("./pages/openloop/ScheduleAppointment")
);
const FunnelOrderComplete = lazy(
  () => import("./pages/create-appointment/FunnelOrderComplete")
);
const Disqualified = lazy(() => import("./pages/Disqualified"));
const Login = lazy(() => import("./pages/Login"));
const MedicationInstructions = lazy(
  () => import("./pages/MedicationInstructions")
);
const MedicationOrderDetails = lazy(
  () => import("./pages/MedicationOrderDetails")
);
const Messages = lazy(() => import("./pages/Messages"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const PrescriptionDetails = lazy(() => import("./pages/PrescriptionDetails"));
const Rate = lazy(() => import("./pages/Rate"));
const ReportSideEffects = lazy(() => import("./pages/ReportSideEffects"));
const RescheduleAppt = lazy(() => import("./pages/RescheduleAppt"));
const RescheduleOpenloop = lazy(() => import("./pages/RescheduleOpenloopAppt"));
const SideEffectDetails = lazy(() => import("./pages/SideEffectDetails"));
const SmdSelfServeMigrationPage = lazy(
  () => import("./pages/SmdSelfServeMigrationPage")
);
const StartLabWork = lazy(() => import("./pages/StartLabWork"));
const TreatmentPlan = lazy(() => import("./pages/TreatmentPlan"));
const PaymentMethodsPage = lazy(
  () => import("./pages/account/PaymentMethodsPage")
);
const PaymentMethodsAddPage = lazy(
  () => import("./pages/account/PaymentMethodsAddPage")
);
const Tabs = lazy(() => import("./tabs/Tabs"));
const AddNewCard = lazy(() => import("./pages/AddNewCard"));
const MedicalCitations = lazy(() => import("./pages/MedicalCitations"));
const MembershipManagementSwitcher = lazy(
  () => import("./pages/MembershipManagementSwitcher")
);
const NutritionIntroPage = lazy(() => import("./pages/NutritionIntroPage"));
const MealCardPage = lazy(() => import("./pages/nutrition/MealCardPage"));
const RecipeLibrary = lazy(
  () => import("./pages/nutrition/recipeLibrary/RecipeLibrary")
);
const RecipesFilters = lazy(
  () => import("./pages/nutrition/recipeLibrary/RecipesFilters")
);
const RecipeInLibraryCard = lazy(
  () => import("./pages/nutrition/recipeLibrary/RecipeInLibraryCard")
);
const RefillIntake = lazy(() => import("./pages/RefillIntake"));
// const AddShippingAddress = lazy(() => import("./pages/AddShippingAddress"));
const UpdateShippingAddress = lazy(
  () => import("./pages/UpdateShippingAddress")
);

const SurveyQuestion = lazy(() => import("./pages/survey/SurveyQuestion"));
const PersonalDetails = lazy(
  () => import("./pages/personal-details/PersonalDetails")
);
const CheckoutApproval = lazy(
  () => import("./pages/checkout/approval/CheckoutApproval")
);

const ResultsRouter = lazy(() => import("./pages/checkout/ResultsRouter"));

const SmsSmartRecovery = lazy(() => import("./components/SmsSmartRecovery"));

const PostPurchaseReturn = lazy(
  () => import("./pages/checkout/PostPurchaseReturn")
);

const TelemedicineMedicationAgreement = lazy(
  () => import("./pages/create-appointment/TelemedicineMedicationAgreement")
);

const AsyncIntake = lazy(() => import("./pages/async-funnel/AsyncIntake"));
const AsyncConfirmation = lazy(
  () => import("./pages/async-funnel/AsyncConfirmation")
);
const AppleFitnessLanding = lazy(
  () => import("./pages/AppleFitness/AppleFitnessLanding")
);

import "@mantine/dates/styles.css";
import "@mantine/charts/styles.css";
// Customer google typeface
import "typeface-inter";
import "typeface-syne";
import "@fontsource/lora";
import "@fontsource/lora/500.css";
import "@fontsource/instrument-serif";
/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
import "@mantine/code-highlight/styles.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/* Theme variables */
import { SplashScreen } from "@capacitor/splash-screen";
import "@mantine/carousel/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "./theme/variables.css";
import ScheduleAppt from "./pages/ScheduleAppt";
import PrivateRoute from "./components/PrivateRoute";
import { CheckoutRoutes, SmsSmartRecoveryRoutes } from "./typings/enums";
import SurveyRoute from "./components/SurveyRoute";
import { PostHogProvider } from "./providers/PostHogProvider";
import { AnalyticsProvider } from "./providers/AnalyticsProvider";
import { AnalyticsPageTracker } from "./components/AnalyticsPageTracker";
import ConditionalAppUrlListener from "./components/ConditionalAppUrlListener";
import { AppRoutes } from "./constants/routesConstants";
import SmdMigrationGate from "./components/SmdMigrationGate";
import SmdMigrationContactPage from "./pages/SmdMigrationContactPage";
import { isMigrationEntryPoint } from "./utils/smdMigrationInterception";

const UploadLabs = lazy(() => import("./pages/UploadLabs"));
const InitialAsyncLanding = lazy(() => import("./pages/InitialAsyncLanding"));
const WhatYouNeedToComplete = lazy(
  () => import("./pages/WhatYouNeedToComplete")
);

const GatedReportSideEffects: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="side_effects">
    <ReportSideEffects />
  </SmdMigrationGate>
);

const GatedRefillIntake: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="refill_questionnaire">
    <RefillIntake />
  </SmdMigrationGate>
);

const GatedClinicalUpload: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="provider_chat">
    <UploadLabs />
  </SmdMigrationGate>
);

const GatedCreateAppointment: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="appointment">
    <CreateAppointment />
  </SmdMigrationGate>
);

const GatedAppointmentSelector: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="appointment">
    <AppointmentSelector />
  </SmdMigrationGate>
);

const GatedScheduleAppt: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="appointment">
    <ScheduleAppt />
  </SmdMigrationGate>
);

const GatedRescheduleAppt: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="appointment">
    <RescheduleAppt />
  </SmdMigrationGate>
);

const GatedStartLabWork: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="lab_work">
    <StartLabWork />
  </SmdMigrationGate>
);

const GatedPaymentMethods: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="payment_method">
    <PaymentMethodsPage />
  </SmdMigrationGate>
);

const GatedPaymentMethodsAdd: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="payment_method">
    <PaymentMethodsAddPage />
  </SmdMigrationGate>
);

const GatedAddNewCard: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="payment_method">
    <AddNewCard />
  </SmdMigrationGate>
);

const GatedInitialAsync: React.FC<RouteComponentProps> = () => (
  <SmdMigrationGate entryPoint="additional_setup">
    <InitialAsyncLanding />
  </SmdMigrationGate>
);

const SmdMigrationContactRoute: React.FC<RouteComponentProps> = ({ match }) => {
  const { entryPoint } = match.params as { entryPoint?: string };

  return entryPoint && isMigrationEntryPoint(entryPoint) ? (
    <SmdMigrationContactPage entryPoint={entryPoint} />
  ) : (
    <Redirect to="/tabs/home" />
  );
};

setupIonicReact({
  animated: true, //!isPlatform('mobileweb'),
});

// Sanitize URL to remove query params and replace path IDs
const sanitizeUrl = (url: string | undefined): string => {
  // Short-circuit if no URL or already unknown
  if (!url || url === "unknown") return "unknown";

  try {
    const parsed = new URL(url, window.location.origin);
    // Remove query params and hash
    let pathname = parsed.pathname;
    // Replace UUID/ID patterns with :id placeholder
    pathname = pathname.replace(
      /\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
      "/:id"
    );
    // Replace numeric IDs with :id placeholder
    pathname = pathname.replace(/\/\d+/g, "/:id");
    return pathname;
  } catch {
    return "unknown";
  }
};

// Sanitize error message to remove potential PII
const sanitizeMessage = (message: string | undefined): string => {
  if (!message) return "Unknown error";
  // Truncate long messages
  let sanitized = message.substring(0, 200);
  // Remove email patterns
  sanitized = sanitized.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    "[email]"
  );
  // Remove phone patterns
  sanitized = sanitized.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, "[phone]");
  // Remove long hex strings (tokens)
  sanitized = sanitized.replace(/\b[a-f0-9]{32,}\b/gi, "[token]");
  return sanitized;
};

// Track React Query error stats per endpoint + operation type
interface EndpointErrorStats {
  firstSeen: number;
  count: number;
  lastStatus: number;
  lastMessage: string;
}

const endpointErrors = new Map<string, EndpointErrorStats>();

const trackReactQueryError = (error: unknown, type: "query" | "mutation") => {
  if (!datadogRum.getInitConfiguration()) return;

  // Type guard for axios-like error structure
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;
  const endpoint = sanitizeUrl(
    err?.config?.url ||
      err?.response?.config?.url ||
      err?.request?.responseURL ||
      err?.request?.url ||
      err?.responseURL ||
      err?.cause?.url
  );
  const status = err?.response?.status || err?.status;
  const message = sanitizeMessage(err?.message);
  const lastUserAction = getLastUserAction();
  const errorForDatadog =
    err instanceof Error ? err : new Error(message || "react_query_error");

  // Keep query and mutation errors separate, even when they share the same endpoint.
  const bucketKey = `${type}:${endpoint}`;
  const existing = endpointErrors.get(bucketKey);
  const now = Date.now();

  if (!existing) {
    // First error - log immediately
    datadogRum.addAction(`react_query_${type}_error`, {
      endpoint,
      status,
      message,
      error_count: 1,
      type,
    });
    datadogRum.addError(errorForDatadog, {
      error_origin: "react_query",
      endpoint,
      operation_type: type,
      http_status: status,
      last_user_action: lastUserAction,
      message,
      error_count: 1,
    });

    endpointErrors.set(bucketKey, {
      firstSeen: now,
      count: 1,
      lastStatus: status,
      lastMessage: message,
    });
  } else {
    // Increment count
    existing.count += 1;
    existing.lastStatus = status;
    existing.lastMessage = message;

    // Log update every 10th error or every 5 minutes
    const timeSinceFirst = now - existing.firstSeen;
    if (existing.count % 10 === 0 || timeSinceFirst > 300000) {
      datadogRum.addAction(`react_query_${type}_error_update`, {
        endpoint,
        status: existing.lastStatus,
        message: existing.lastMessage,
        error_count: existing.count,
        type,
        time_window_mins: Math.round(timeSinceFirst / 60000),
      });
      datadogRum.addError(errorForDatadog, {
        error_origin: "react_query",
        endpoint,
        operation_type: type,
        http_status: existing.lastStatus,
        last_user_action: lastUserAction,
        message: existing.lastMessage,
        error_count: existing.count,
        time_window_mins: Math.round(timeSinceFirst / 60000),
      });

      // Reset window
      existing.firstSeen = now;
      // Start the next window from 0 so the next update fires exactly on the 10th error.
      existing.count = 0;
    }
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => trackReactQueryError(error, "query"),
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => trackReactQueryError(error, "mutation"),
  }),
});

const App: React.FC = () => {
  useAppUpdate();
  useEffect(() => {
    SplashScreen.hide();
  }, []);

  useEffect(() => {
    initStorage();
  }, []);

  return (
    <PostHogProvider>
      <AnalyticsProvider>
        <IonApp
          data-qa-target="futurhealth-plus"
          data-qa-page="app"
          data-qa-state="mounted"
        >
          <QueryClientProvider client={queryClient}>
            <NutritionProvider>
              <StorageProvider>
                <IonReactRouter>
                  <BrazeProvider>
                    <AnalyticsPageTracker />
                    <ToastProvider>
                      <ConditionalAppUrlListener>
                        <Suspense fallback={null}>
                          <IonRouterOutlet data-qa-region="routes">
                            {/* Authed routes */}
                            <PrivateRoute exact path="/rate" component={Rate} />
                            <PrivateRoute
                              exact
                              path="/treatment-plan/:prescriptionId?/:state?"
                              component={TreatmentPlan}
                            />
                            <PrivateRoute
                              exact
                              path="/medication-instructions/:medicationId?"
                              component={MedicationInstructions}
                            />
                            <PrivateRoute
                              exact
                              path="/prescription-details/:medicationId?"
                              component={PrescriptionDetails}
                            />
                            <PrivateRoute
                              exact
                              path="/after-visit-summaries"
                              component={AfterVisitSummaries}
                            />
                            <PrivateRoute
                              exact
                              path={`${AppRoutes.CREATE_APPOINTMENT}/:episodeId?`}
                              component={GatedCreateAppointment}
                            />
                            <PrivateRoute
                              exact
                              path={`${AppRoutes.CREATE_APPOINTMENT_SCHEDULE}/:consultId?/:episodeId?`}
                              component={GatedAppointmentSelector}
                            />
                            <PrivateRoute
                              path={`${AppRoutes.SCHEDULE_OL_APPOINTMENT}/:step`}
                              component={ScheduleAppointment}
                              exact
                            />
                            <PrivateRoute
                              exact
                              path={AppRoutes.CREATE_APPOINTMENT_ORDER_COMPLETE}
                              component={FunnelOrderComplete}
                            />
                            <PrivateRoute
                              exact
                              path="/telemedicine-agreement"
                              component={TelemedicineMedicationAgreement}
                            />
                            <PrivateRoute
                              exact
                              path="/schedule-appointment/:index/:selectedDate?"
                              component={GatedScheduleAppt}
                            />
                            <PrivateRoute
                              exact
                              path="/reschedule-appointment/:index/:selectedDate?"
                              component={GatedRescheduleAppt}
                            />
                            <PrivateRoute
                              exact
                              path="/reschedule-ol-appointment/:step?/:selectedDate?"
                              component={RescheduleOpenloop}
                            />
                            <PrivateRoute path="/tabs" component={Tabs} />
                            <PrivateRoute
                              exact
                              path="/checkout/return"
                              component={CheckoutReturn}
                            />
                            <PrivateRoute
                              path="/appointment/details/:guid"
                              component={AppointmentDetails}
                            />
                            <PrivateRoute
                              path="/appointment-details"
                              component={AppointmentDetailsOL}
                            />
                            <PrivateRoute
                              path="/medication-order/:status/:orderNumber?"
                              component={MedicationOrderDetails}
                            />
                            <PrivateRoute
                              path="/side-effects"
                              component={SideEffectDetails}
                            />
                            <PrivateRoute
                              path="/start-lab-work/:guid?/:downloadPdfOnLoad?"
                              component={GatedStartLabWork}
                              exact
                            />
                            <PrivateRoute
                              path="/report-side-effects"
                              component={GatedReportSideEffects}
                            />
                            <PrivateRoute
                              path="/after-visit-summaries/details/:consultId"
                              component={AfterVisitSummaryDetails}
                              exact
                            />
                            <PrivateRoute
                              exact
                              path="/notifications"
                              component={Notifications}
                            />
                            <PrivateRoute
                              exact
                              path="/notification-settings"
                              component={NotificationSettings}
                            />
                            <PrivateRoute
                              exact
                              path="/nutrition-quiz"
                              component={NutritionIntroPage}
                            />
                            <PrivateRoute
                              path="/personal-details"
                              component={PersonalDetails}
                            />
                            <PrivateRoute
                              path="/reset-password"
                              component={ResetPassword}
                            />
                            <PrivateRoute
                              path="/add-shipping-address"
                              component={UpdateShippingAddress}
                            />
                            <PrivateRoute
                              path="/messages"
                              component={Messages}
                            />
                            <PrivateRoute
                              exact
                              path="/add-new-card"
                              component={GatedAddNewCard}
                            />
                            <PrivateRoute
                              exact
                              path="/add-payment-method"
                              component={GatedPaymentMethodsAdd}
                            />
                            <PrivateRoute
                              exact
                              path="/payment-methods"
                              component={GatedPaymentMethods}
                            />
                            <PrivateRoute
                              exact
                              path="/medical-citations"
                              component={MedicalCitations}
                            />
                            <PrivateRoute
                              exact
                              path="/membership-management"
                              component={MembershipManagementSwitcher}
                            />
                            <PrivateRoute
                              path="/refill-funnel/:id?/:index"
                              component={GatedRefillIntake}
                              exact
                            />
                            <PrivateRoute
                              path="/migration-support/:entryPoint"
                              component={SmdMigrationContactRoute}
                              exact
                            />
                            <PrivateRoute
                              path={AppRoutes.SELF_SERVE_MIGRATION}
                              component={SmdSelfServeMigrationPage}
                              exact
                            />
                            <PrivateRoute
                              path="/async-funnel/:asyncSessionId/:questionSlug"
                              component={AsyncIntake}
                              exact
                            />
                            <PrivateRoute
                              path="/async-confirmation/:asyncSessionId"
                              component={AsyncConfirmation}
                              exact
                            />
                            <PrivateRoute
                              exact
                              path="/nutrition-meal-card/:mealIndex/:date/:weekNumberInString/:weekDay"
                              component={MealCardPage}
                            />
                            <PrivateRoute
                              exact
                              path="/recipes-library/:swapMealId/:swapMealLabel/:selectedWeek/"
                              component={RecipeLibrary}
                            />
                            <PrivateRoute
                              exact
                              path="/recipe-card"
                              component={RecipeInLibraryCard}
                            />
                            <PrivateRoute
                              exact
                              path="/recipes-filters"
                              component={RecipesFilters}
                            />
                            <PrivateRoute
                              exact
                              path="/upload-files"
                              component={GatedClinicalUpload}
                            />
                            {/* Redirect old route to new route for backwards compatibility */}
                            <Route exact path="/upload-labs">
                              <Redirect to="/upload-files" />
                            </Route>
                            <PrivateRoute
                              exact
                              path="/initial-async"
                              component={GatedInitialAsync}
                            />
                            <PrivateRoute
                              exact
                              path={AppRoutes.WHAT_YOU_NEED_TO_COMPLETE}
                              component={WhatYouNeedToComplete}
                            />
                            <PrivateRoute
                              exact
                              path="/apple-fitness"
                              component={AppleFitnessLanding}
                            />
                            {/* Publicly accessible routes */}
                            <SurveyRoute
                              path="/survey/:slug/:index"
                              component={SurveyQuestion}
                            />
                            <Route
                              path="/disqualified"
                              component={Disqualified}
                            />
                            <Route
                              exact
                              path="/create-account"
                              component={CreateAccount}
                            />
                            <Route
                              exact
                              path="/activate-notifications"
                              component={ActivateNotifications}
                            />
                            <Route exact path="/login" component={Login} />
                            <Route
                              exact
                              path={CheckoutRoutes.Approval}
                              component={CheckoutApproval}
                            />
                            <Route
                              exact
                              path={`${CheckoutRoutes.Results}/:medicationSlug`}
                              component={ResultsRouter}
                            />
                            {/*
                              The password-set screen is gone, but two live
                              entry points still hand checkout a `return_url`
                              of `/account` — the SMS recovery offer and the
                              results router, both via `getRedirectUrl()`. A
                              returning payer arrives here with the
                              `firebase_token` that authenticates them, so the
                              path keeps a component to spend it and move them
                              on. Retire it with the constant.
                            */}
                            <Route
                              exact
                              path={CheckoutRoutes.Account}
                              component={PostPurchaseReturn}
                            />
                            <Route
                              exact
                              path={SmsSmartRecoveryRoutes.Offer}
                              component={SmsSmartRecovery}
                            />
                            <Route exact path="/">
                              <Redirect to="/tabs" />
                            </Route>
                          </IonRouterOutlet>
                        </Suspense>
                      </ConditionalAppUrlListener>
                    </ToastProvider>
                  </BrazeProvider>
                </IonReactRouter>
              </StorageProvider>
            </NutritionProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </IonApp>
      </AnalyticsProvider>
    </PostHogProvider>
  );
};

export default App;
